import type { ItemAyuda } from "@/ui/AyudaVista";

/**
 * Piezas de Ayuda que se repiten en las ventanas donde se redacta para alguien
 * de fuera: «Enviar correo», «Responder al proveedor» y abrir ticket en Jimi o
 * en Queclink. Mismo componente en pantalla → misma explicación, escrita una
 * sola vez. Si cambias el componente, cambia esto.
 */

/** La lista «Correos del caso» (`components/correo/CorreosParaIA`). Solo existe
 *  con la IA habilitada para el usuario (`ai_comentarios_enabled`). */
export const BOTONES_CORREOS_IA: ItemAyuda[] = [
  {
    label: "Correos del caso (N)",
    text: "Abre o cierra la lista de correos que entraron y salieron del caso. Al lado dice cuántos va a leer la IA («ninguno va a la IA», «2 de 5 van a la IA»).",
  },
  {
    label: "Switch de cada correo",
    text: "Prendido, la IA lee ese correo al redactar, además de la bitácora. Arrancan apagados: tú decides qué material entra. Nada de esos correos se manda tal cual.",
  },
  {
    label: "Marcar todos / Quitar todos",
    text: "Prende o apaga de un toque todos los correos de la lista.",
  },
];

export const FICHAS_CORREOS_IA: ItemAyuda[] = [
  { label: "Recibido / Enviado", text: "Si el correo nos llegó o si lo mandamos nosotros." },
  {
    label: "reenvío de …",
    text: "El correo es un reenvío: dice quién escribió el mensaje de adentro.",
  },
  { label: "Clip con nombres", text: "Los archivos que traía ese correo, todos por su nombre." },
];

/**
 * «Apuntes del caso» y los dos botones de evidencias de las ventanas de
 * proveedor (responder y abrir ticket en Queclink).
 *
 * `destino` es dónde cae lo que se agrega: «la respuesta», «la descripción».
 */
export function botonesApuntesYEvidencias(destino: string): ItemAyuda[] {
  return [
    {
      label: "Apuntes del caso (N)",
      text: "Abre o cierra las notas escritas a mano en el caso (las 30 más recientes), para consultarlas sin salir de la ventana.",
    },
    {
      label: "+ agregar",
      text: `Pega el texto de ese apunte al final de ${destino}. Después lo puedes editar.`,
    },
    {
      label: "📎 Agregar link de evidencias",
      text: `Pega al final de ${destino} el enlace del paquete ZIP más reciente del caso que siga vigente. Si ese ZIP tiene contraseña, la contraseña no se agrega: escríbela tú.`,
    },
    {
      label: "📦 Generar paquete de evidencias",
      text: "Sale cuando el caso tiene evidencias pero ningún ZIP vigente. Arma un ZIP sin contraseña con todas las evidencias del caso —también las fotos de la bitácora y lo que mandó el proveedor— y en su lugar aparece «📎 Agregar link de evidencias».",
    },
  ];
}

/**
 * Barra de formato del editor de correo (`components/RichTextEditor`).
 * `conVentanaImagen`: el padre abre su propia ventana de imagen (Enviar correo),
 * que sube a Evidencias las de más de 1 MB en vez de rechazarlas.
 */
export function botonesEditor(conVentanaImagen: boolean): ItemAyuda[] {
  return [
    { label: "B · I · S", text: "Negrita, itálica y tachado del texto seleccionado." },
    { label: "• · 1.", text: "Lista con viñetas y lista numerada." },
    { label: "❝", text: "Marca el párrafo como cita." },
    {
      label: "🔗",
      text: "Pone un enlace en el texto seleccionado. Si ya hay uno, lo cambia; con la dirección vacía, lo quita.",
    },
    {
      label: "🖼",
      text: conVentanaImagen
        ? "Inserta una imagen. Hasta 1 MB va dentro del mensaje; las más pesadas se guardan en las evidencias del caso y en el mensaje queda una nota con su nombre (para mandarla, métela en un paquete ZIP)."
        : "Inserta una imagen dentro del mensaje, de hasta 1 MB.",
    },
    {
      label: "⌫",
      text: "Limpia el formato: deja el texto seleccionado como texto normal, sin negritas, listas ni citas.",
    },
  ];
}

/** Atajos del mismo editor (TipTap 2: verificados en sus extensiones). */
export const ATAJOS_EDITOR: ItemAyuda[] = [
  { label: "Ctrl + B / Ctrl + I", text: "Negrita / itálica, con el cursor en el editor (⌘ en Mac)." },
  { label: "Ctrl + Shift + S", text: "Tachado." },
  { label: "Ctrl + Shift + 8 / 7", text: "Lista con viñetas / lista numerada." },
  { label: "Ctrl + Shift + B", text: "Cita." },
];
